/**
 * Where everything on the menu screens sits, in HUD pixels (640x360, y down). Pure numbers, shared
 * by the model (which row the mouse is over) and the painter, so the two cannot disagree.
 */

import type { TITLE_LAYOUT } from '../../render/title';
import { HUD_W, PANEL_W } from '../hud/panels';
import type { MenuScreen } from './index';

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export const inside = (r: Rect, x: number, y: number): boolean =>
  x >= r.x && x < r.x + r.w && y >= r.y && y < r.y + r.h;

/**
 * The title scene's menu band. Copied rather than imported so the menus load without three.js;
 * the type is the contract's own literal type, so a change on either side stops the build.
 */
export const BAND: typeof TITLE_LAYOUT = { menuTop: 176, menuBottom: 300, menuWidth: 224 };

/** The playfield between the two side panels, which every screen but the title covers. */
export const PLAY: Rect = { x: PANEL_W, y: 0, w: HUD_W - 2 * PANEL_W, h: 360 };
export const CX = HUD_W / 2;

/** Screen headings: double-size text at this y. */
export const HEADING_Y = 14;
/** The key hints at the foot of a covering screen. */
export const FOOTER_Y = 340;

/** The title's five items, double size, spaced to fill the band. */
export const TITLE_SCALE = 2;
export const TITLE_PITCH = 24;
export const titleY = (i: number): number => BAND.menuTop + 2 + i * TITLE_PITCH;
/** The version, bottom right under the 2UP panel: its text's right edge and top. */
export const VERSION_RIGHT = HUD_W - 8;
export const VERSION_Y = 340;

/** Options: the label and value columns, and where the value's "<" sits (a click there steps left). */
export const LABEL_X = PLAY.x + 40;
export const VALUE_X = 320;
export const LESS_X = VALUE_X - 16;
export const OPTION_Y0 = 40;
export const OPTION_PITCH = 16;
/** Options' help line and note. */
export const HELP_Y = 276;
export const NOTE_Y = 300;

/** Controls: each player's four rows under a heading, then reset and back, a note, the pad's layout. */
export const CONTROL_PITCH = 12;
export const CONTROL_HEADS = [40, 108] as const;
export const CONTROL_KEYS_X = PLAY.x + 176;
export const CONTROL_NOTE_Y = 208;
export const PAD_Y = 232;

/** Credits and the change list. */
export const CREDIT_Y0 = 40;
export const CREDIT_PITCH = 12;
export const CHANGE_Y0 = 40;
export const CHANGE_PITCH = 11;
export const CHANGE_ROWS = 24;
/** Characters per change-list line (the pane is 416 px, less a scroll bar). */
export const CHANGE_WIDTH = 50;
export const CHANGE_X = PLAY.x + 16;
export const CHANGE_PANE: Rect = { x: PLAY.x, y: CHANGE_Y0 - 2, w: PLAY.w, h: CHANGE_ROWS * CHANGE_PITCH };

/** The y of each row's text on `screen`, given how many rows it has. */
export function rowYs(screen: MenuScreen, count: number): number[] {
  const ys: number[] = [];
  for (let i = 0; i < count; i++) {
    switch (screen) {
      case 'title':
        ys.push(i < count - 1 ? titleY(i) : VERSION_Y);
        break;
      case 'pause':
        ys.push(150 + i * 18);
        break;
      case 'options':
        // The last two rows (reset, back) stand apart from the values.
        ys.push(OPTION_Y0 + i * OPTION_PITCH + (i >= count - 2 ? 8 : 0));
        break;
      case 'controls':
        if (i < 8) ys.push((CONTROL_HEADS[i < 4 ? 0 : 1]) + 14 + (i % 4) * CONTROL_PITCH);
        else ys.push(CONTROL_HEADS[1] + 14 + 4 * CONTROL_PITCH + 12 + (i - 8) * CONTROL_PITCH);
        break;
      default:
        // credits and changes have one row, BACK, at the foot.
        ys.push(FOOTER_Y - 16 + i * 12);
    }
  }
  return ys;
}

/** The area the mouse picks each row with. */
export function rowRects(screen: MenuScreen, count: number): Rect[] {
  return rowYs(screen, count).map((y, i) => {
    if (screen === 'title') {
      if (i === count - 1) return { x: HUD_W - PANEL_W, y: y - 18, w: PANEL_W, h: 30 };
      return { x: CX - BAND.menuWidth / 2, y: y - 5, w: BAND.menuWidth, h: TITLE_PITCH };
    }
    if (screen === 'pause') return { x: CX - 96, y: y - 5, w: 192, h: 18 };
    if (screen === 'options') return { x: PLAY.x + 8, y: y - 4, w: PLAY.w - 16, h: OPTION_PITCH };
    if (screen === 'controls') return { x: PLAY.x + 8, y: y - 3, w: PLAY.w - 16, h: CONTROL_PITCH };
    return { x: CX - 48, y: y - 4, w: 96, h: 14 };
  });
}
